import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Counter = ({ end, duration = 2000, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const increment = end / (duration / 16);
          let current = 0;
          const timer = setInterval(() => {
            current += increment;
            if (current >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, 16);
          return () => clearInterval(timer);
        }
      },
      { threshold: 0.5 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, [end, duration, hasAnimated]);

  return (
    <span ref={counterRef}>
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

const Stats = () => {
  const { t } = useTranslation();
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const stats = [
    { value: 35, prefix: '+', suffix: '', label: t('stats.yearsExperience') },
    { value: 5, prefix: '', suffix: '', label: t('stats.specializations') },
    { value: 3, prefix: '', suffix: '', label: t('stats.surgicalCenters') },
    { value: 100, prefix: '', suffix: '%', label: t('stats.individualPlanning') },
  ];

  const porqueNorro = [
    { title: t('stats.reason1Title'), description: t('stats.reason1Desc') },
    { title: t('stats.reason2Title'), description: t('stats.reason2Desc') },
    { title: t('stats.reason3Title'), description: t('stats.reason3Desc') },
    { title: t('stats.reason4Title'), description: t('stats.reason4Desc') },
  ];

  return (
    <section ref={sectionRef}>
      {/* Stats bar */}
      <div className="bg-charcoal border-t-[3px] border-gold">
        <div className="container mx-auto px-6 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 animate-on-scroll">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="font-serif text-4xl md:text-5xl text-gold mb-2">
                  <Counter end={stat.value} prefix={stat.prefix} suffix={stat.suffix} />
                </div>
                <p className="text-gold-light/70 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quote section */}
      <div className="py-20 bg-cream">
        <div className="container mx-auto px-6 max-w-4xl text-center animate-on-scroll">
          <div className="w-12 h-px bg-gold mx-auto mb-10"></div>
          <blockquote className="font-serif text-2xl md:text-3xl text-charcoal font-light italic leading-relaxed mb-8 border-none pl-0">
            "{t('stats.quote')}"
          </blockquote>
          <p className="text-gold text-sm tracking-wider uppercase">
            Dr. Ángel Norro
          </p>
        </div>
      </div>

      {/* Por qué el Dr. Norro */}
      <div className="py-24 bg-ivory">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16 animate-on-scroll">
            <p className="eyebrow mb-6">{t('stats.whyNorroEyebrow')}</p>
            <h2 className="font-serif text-4xl md:text-5xl text-charcoal">
              {t('stats.whyNorroTitle')} <span className="text-gold">Dr. Norro</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {porqueNorro.map((item, index) => (
              <div key={index} className="card-luxury p-8 animate-on-scroll">
                <h3 className="font-serif text-xl text-charcoal mb-3">{item.title}</h3>
                <p className="text-text-secondary leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Final */}
      <div className="py-20 bg-cream">
        <div className="container mx-auto px-6 max-w-3xl text-center animate-on-scroll">
          <h3 className="font-serif text-3xl md:text-4xl text-charcoal mb-4">
            {t('stats.ctaTitle')}
          </h3>
          <p className="text-text-secondary mb-10">
            {t('stats.ctaDesc')}
          </p>
          <Link 
            to="/contacto"
            className="btn-gold inline-flex items-center gap-2 group"
          >
            {t('stats.ctaButton')}
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
          <p className="text-text-muted text-sm mt-6">
            {t('stats.ctaNote')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Stats;
